import { useEffect, useState, useMemo, useContext, useDeferredValue } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Sparkle,
  PlayCircle,
  Heart,
  CheckCircle,
  User,
  SquaresFour,
} from "@phosphor-icons/react";
import { CategoriesContext } from "Context/General/CategoriesContext";
import { LoginAuthContext } from "Context/Authentication/LoginAuth";
import IsError from "Components/RequestHandler/IsError";
import Container from "Components/Container/Container";
import ExploreSearch from "./ExploreSearch";
import CategoryChipBar from "./Shelves/CategoryChipBar";
import Shelf from "./Shelves/Shelf";
import ContinueLearningHero from "./Shelves/ContinueLearningHero";
import BASE_URL from "Utilities/BASE_URL";

const ALL_CHIP = "__all__";
const MY_COURSES_CHIP = "my_courses";

const MainCategoryLandingScreen = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { fetchCategories } = useContext(CategoriesContext);
  const { isAuthenticated, token, selectedUser } = useContext(LoginAuthContext);

  const [activeChip, setActiveChip] = useState(ALL_CHIP);

  // Keep context warm for other screens that still read from it. The browse
  // bundle below is what drives this page's UI, so context load doesn't gate
  // anything here.
  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (location?.state?.slug) {
      setActiveChip(`cat:${location.state.slug}`);
    }
  }, [location?.state?.slug]);

  // Profile docs can be keyed as `_id` or `id` depending on source — accept both.
  const profileId = selectedUser?._id || selectedUser?.id;

  // ─── SINGLE BUNDLE REQUEST ────────────────────────────────────────────
  // Replaces: categories + new-this-week + per-category fan-out + my-progress + my-list.
  // One TTFB gate, one JSON parse, atomically consistent slice.
  const bundleQuery = useQuery({
    queryKey: ["browse-bundle", i18n.language, isAuthenticated ? profileId || "anon" : "anon"],
    queryFn: async () => {
      const url = new URL(`${BASE_URL}/${i18n.language}/home/browse-bundle`);
      if (profileId) url.searchParams.set("profile_id", profileId);
      const res = await fetch(url.toString(), {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error("Browse bundle fetch failed");
      const json = await res.json();
      return json?.data || null;
    },
    staleTime: 60 * 1000,
    // keepPreviousData so chip swaps don't flash the skeleton while the
    // next bundle variant (if any) is in flight.
    placeholderData: (prev) => prev,
  });

  const bundle = bundleQuery.data;
  const categories = bundle?.categories || [];
  const newCourses = bundle?.newThisWeek || [];
  const coursesByCategory = bundle?.byCategory || {};
  const continueWatching = bundle?.progress?.continueWatching || [];
  const completedCourses = bundle?.progress?.completedCourses || [];
  const myList = bundle?.myList || [];

  // Progress map derived once per bundle update.
  const progressMap = useMemo(() => {
    const map = {};
    continueWatching.forEach((item) => {
      const id = item?.course?._id;
      if (id) {
        map[id] = {
          percent: Math.min(100, Math.max(0, item?.progressPercent ?? 0)),
          lessonsCompleted: item?.lessonsCompleted ?? 0,
          totalLessons: item?.totalLessons ?? 0,
        };
      }
    });
    completedCourses.forEach((item) => {
      const id = item?._id || item?.course?._id;
      if (id) {
        map[id] = { ...(map[id] || {}), isCompleted: true, percent: 100 };
      }
    });
    return map;
  }, [continueWatching, completedCourses]);

  // Hero = most recent in-progress
  const heroItem = continueWatching.length > 0 ? continueWatching[0] : null;
  const heroCourseId = heroItem?.course?._id;
  const continueWatchingShelfItems = continueWatching.filter(
    (item) => item?.course?._id !== heroCourseId
  );

  // Chip bar
  const chips = useMemo(() => {
    const base = [
      {
        key: ALL_CHIP,
        label: t("library.all") || "All",
        icon: <SquaresFour size={14} />,
      },
    ];
    if (isAuthenticated) {
      base.push({
        key: MY_COURSES_CHIP,
        label: t("library.my_courses") || "My Courses",
        icon: <User size={14} />,
      });
    }
    base.push({
      key: "new",
      label: t("library.new") || "New",
      icon: <Sparkle size={14} />,
    });
    (categories || []).forEach((cat) => {
      base.push({ key: `cat:${cat.slug}`, label: cat.title });
    });
    return base;
  }, [categories, isAuthenticated, t]);

  // Chip click feels instant; shelf re-render uses the deferred chip so React
  // keeps input responsive while rails swap.
  const deferredChip = useDeferredValue(activeChip);

  if (bundleQuery.isError) return <IsError />;

  const showAll = deferredChip === ALL_CHIP;
  const showMyCourses = showAll || deferredChip === MY_COURSES_CHIP;
  const showNew = showAll || deferredChip === "new";
  const activeCatSlug = deferredChip.startsWith("cat:")
    ? deferredChip.replace("cat:", "")
    : null;

  const myCoursesEmpty =
    deferredChip === MY_COURSES_CHIP &&
    continueWatching.length === 0 &&
    myList.length === 0 &&
    completedCourses.length === 0;

  return (
    <div className="pb-16 lg:pb-primary pt-pageTop lg:pt-primary">
      <Container>
        {/* Page title + search */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <h1 className="shrink-0 text-3xl font-bold text-white sm:text-4xl">
            {t("general.explore")}
          </h1>
          <ExploreSearch />
        </div>

        {/* Chip bar */}
        <CategoryChipBar
          chips={chips}
          activeChip={activeChip}
          onSelect={setActiveChip}
        />

        {/* Smart Continue Learning hero */}
        {isAuthenticated && heroItem && (showAll || showMyCourses) && (
          <div className="mt-6">
            <ContinueLearningHero resumeItem={heroItem} />
          </div>
        )}

        {/* ─── MY COURSES SECTION ─── */}
        {showMyCourses && isAuthenticated && (
          <>
            <Shelf
              title={t("library.continue_learning") || "Continue Learning"}
              icon={<PlayCircle size={20} className="text-primary" weight="fill" />}
              items={continueWatchingShelfItems}
              progressMap={progressMap}
              lazyPaint={false}
            />

            <Shelf
              title={t("library.my_list") || "My List"}
              icon={<Heart size={20} className="text-primary" weight="fill" />}
              items={myList}
              progressMap={progressMap}
              lazyPaint={false}
            />

            <Shelf
              title={t("library.completed") || "Completed"}
              icon={<CheckCircle size={20} className="text-green-400" weight="fill" />}
              items={completedCourses}
              progressMap={progressMap}
            />

            {myCoursesEmpty && (
              <div className="mt-16 text-center">
                <p className="text-white/60 text-sm">
                  {t("library.my_courses_empty") ||
                    "Start or save a course to see it here"}
                </p>
              </div>
            )}
          </>
        )}

        {/* ─── NEW THIS WEEK ─── */}
        {showNew && newCourses.length > 0 && (
          <Shelf
            title={t("library.new_this_week") || "New This Week"}
            icon={<Sparkle size={20} className="text-primary" weight="fill" />}
            items={newCourses}
            progressMap={progressMap}
            lazyPaint={!showMyCourses || !isAuthenticated}
          />
        )}

        {/* ─── ALL: CATEGORY RAILS ─── */}
        {showAll &&
          (categories || []).map((cat) => {
            const courses = coursesByCategory[cat._id] || [];
            if (courses.length === 0) return null;
            return (
              <Shelf
                key={cat._id}
                title={cat.title}
                items={courses}
                progressMap={progressMap}
              />
            );
          })}

        {/* ─── SINGLE CATEGORY VIEW ─── */}
        {activeCatSlug &&
          (() => {
            const cat = (categories || []).find(
              (c) => c.slug === activeCatSlug
            );
            if (!cat) return null;
            const courses = coursesByCategory[cat._id] || [];
            if (courses.length === 0) {
              return (
                <div className="mt-16 text-center">
                  <p className="text-white/60 text-sm">
                    {t("library.no_courses_in_category") ||
                      "No courses in this category yet"}
                  </p>
                </div>
              );
            }
            return (
              <Shelf
                title={cat.title}
                items={courses}
                progressMap={progressMap}
              />
            );
          })()}
      </Container>
    </div>
  );
};

export default MainCategoryLandingScreen;
